import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';

// icons
import { PiSquaresFourThin } from "react-icons/pi";
import { FaWallet } from "react-icons/fa";
import { AiOutlineStock } from "react-icons/ai";
import { RiFileList2Fill } from "react-icons/ri";
import { AiOutlineHistory } from "react-icons/ai";
import { IoIosSettings } from "react-icons/io";
import { MdHelp } from "react-icons/md";
import { IoLogOut } from "react-icons/io5";
import { FaUsers } from "react-icons/fa";
import { FaBalanceScale } from "react-icons/fa";
import { IoChevronBack, IoChevronForward } from "react-icons/io5"; // For collapse/expand toggle
import { IoCalculatorSharp } from "react-icons/io5";

interface Props { }

function SideBar(props: Props) {
  const [collapsed, setCollapsed] = useState(false);

  const menuList = [
    {
      to: "/",
      icon: <PiSquaresFourThin />,
      label: "Dashboard"
    },
    {
      to: "/invoice",
      icon: <RiFileList2Fill />,
      label: "Invoice"
    },
    {
      to: "/add-party",
      icon: <FaWallet />,
      label: "Add Party"
    },
    {
      to: "/Stock",
      icon: <AiOutlineStock />,
      label: "Add Stock"
    },
    {
      to: "/stocks-name",
      icon: <AiOutlineStock />,
      label: "Stock Management"
    },
    {
      to: "/BalanceSheet",
      icon: <FaBalanceScale />,
      label: "Balance Sheet"
    },
    {
      to: "/parties-list",
      icon: <RiFileList2Fill />,
      label: "Parties List"
    },
    {
      to: "/history",
      icon: <AiOutlineHistory />,
      label: "History"
    },
    {
      to: "/users",
      icon: <FaUsers />,
      label: "Users"
    },
    {
      to: "/usershistory",
      icon: <FaUsers />,
      label: "Users History"
    }
  ];

  const generalList = [
    {
      to: "/interest-calc",
      icon: <IoCalculatorSharp />,
 
      label: "Interest Calc"
    },
    {
      to: "/setting",
      icon: <IoIosSettings />,
      label: "Settings"
    },
    {
      to: "/calculation",
      icon: <IoCalculatorSharp />,
      label: "Calculation"
    },
    {
      to: "/help",
      icon: <MdHelp />,
      label: "Help"
    },
    {
      to: "/logout",
      icon: <IoLogOut />,
      label: "Logout"
    }
  ];

  // New color scheme: blue/gray neutrals, primary green, hint of slate
  const MAIN_BG = "bg-[#f5f7fa]";
  const MAIN_BORDER = "border-[#e3e8ef]";
  const ACCENT_GREEN = "#246B51";
  const MUTED_TEXT = "text-[#61728a]";
  const ACTIVE_BG = "bg-[#e6f4f1]";
  const ACTIVE_ICON_BG = "bg-[#21836C]";
  const ACTIVE_ICON_COLOR = "text-white";
  const ICON_DEFAULT = "text-[#246B51]";
  const ICON_HOVER = "group-hover:text-[#176A4E]";
  const ACTIVE_LABEL = "text-[#21836C]";
  const ACTIVE_LABEL_FONT = "font-bold";
  const SECTION_HEADER = "text-[#5B6C7F]";
  const SECTION_HEADER_MUTED = "text-[#9ba2b7]";

  return (
    <aside
      className={
        `sticky top-0 z-40 bottom-0 h-screen shadow-2xl ${MAIN_BG} transition-all duration-300
        ${collapsed
          ? "w-[64px] pl-2 pr-2"
          : "w-[220px] md:w-[200px] lg:w-[18%] pl-4 pr-2"
        } flex flex-col justify-between border-r ${MAIN_BORDER} pt-0`
      }
      style={{
        boxShadow: "0 6px 32px 0 #e6ecef, 4px 0 24px 0 #edf5ef inset"
      }}
    >
      <div className="flex-1 flex flex-col justify-between min-h-0">
        <div className="flex flex-col min-h-screen"
          style={{
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none"
          }}
        >
          {/* Hide scrollbar in Webkit browsers */}
          <style>
            {`
              aside > div > div::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
          {/* Collapse/Expand button */}
          <div className="flex items-center justify-end mb-2 mt-4 mr-2">
            <button
              className="rounded-full border-2 border-[#e1e4ea] bg-white hover:bg-[#e3ede7] shadow p-1.5 transition-all duration-200"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              type="button"
            >
              {collapsed
                ? <IoChevronForward className="text-xl text-[#21836C]" />
                : <IoChevronBack className="text-xl text-[#21836C]" />
              }
            </button>
          </div>
          <p className={`text-xs font-semibold tracking-widest ${SECTION_HEADER} ml-2 mb-2 uppercase transition-opacity duration-200 ${collapsed ? 'opacity-0 w-0 h-0 overflow-hidden' : 'opacity-95'}`}>Menu</p>
          <nav className="flex flex-col gap-1">
            {menuList.map(({ to, icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `group flex items-center gap-3 py-3 pl-2 pr-2 rounded-lg
                    transition-all
                    ${isActive
                      ? `${ACTIVE_BG} shadow`
                      : "hover:bg-[#f0f5fa] hover:shadow"
                    } 
                    ${collapsed ? "justify-center px-0 rounded-full" : ""}
                  `
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`min-w-[38px] min-h-[38px] flex items-center justify-center transition-all duration-200 rounded-md 
                        ${isActive
                          ? `${ACTIVE_ICON_BG} shadow scale-105`
                          : "bg-white border border-[#DBE2EA] scale-100"
                        }
                      `}
                      style={{
                        boxShadow: isActive ? "0 4px 14px 0 #b9f2d0" : undefined,
                        border: isActive ? "none" : undefined
                      }}
                    >
                      <span className={`${isActive ? ACTIVE_ICON_COLOR : ICON_DEFAULT + ' ' + ICON_HOVER} text-xl`}>
                        {icon}
                      </span>
                    </div>
                    <span
                      className={
                        `whitespace-nowrap ml-1 font-medium transition-all duration-200
                        ${collapsed ? 'opacity-0 w-0 h-0 overflow-hidden' : 'opacity-100'}
                        ${isActive ? ACTIVE_LABEL + ' ' + ACTIVE_LABEL_FONT : `${MUTED_TEXT} group-hover:text-[#21836C]`}
                        text-[16px]`
                      }
                      style={collapsed ? { width: 0, padding: 0, margin: 0 } : { letterSpacing: "0.01rem" }}
                    >
                      {label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
           {/* Ensures GENERAL section is always visible on small screens */}
        <div className="pt-1 pb-6 border-t border-[#dee7f1] mt-6 min-h-[1px]">
          <p className={`text-xs font-semibold tracking-widest ${SECTION_HEADER_MUTED} ml-2 uppercase mb-2 transition-all duration-200 ${collapsed ? 'opacity-0 w-0 h-0 overflow-hidden' : 'opacity-85'}`}>General</p>
          <nav className="flex flex-col gap-1">
            {generalList.map(({ to, icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `group flex items-center gap-3 py-3 pl-2 pr-2 rounded-lg
                  transition-all
                  ${isActive
                    ? "bg-[#e6e8ea] shadow"
                    : "hover:bg-[#f2f4f7] hover:shadow"
                  }
                  ${collapsed ? "justify-center px-0 rounded-full" : ""}
                  `
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`min-w-[38px] min-h-[38px] flex items-center justify-center transition-all duration-200 rounded-md 
                        ${isActive
                          ? "bg-[#a6b4c4] shadow scale-105"
                          : "bg-white border border-[#DBE2EA] scale-100"
                        }
                      `}
                      style={{
                        boxShadow: isActive ? "0 2px 8px 0 #d9e5f0" : undefined,
                        border: isActive ? "none" : undefined
                      }}
                    >
                      <span className={`${isActive ? 'text-[#fff]' : 'text-[#43718a] group-hover:text-[#21836C]'} text-xl`}>
                        {icon}
                      </span>
                    </div>
                    <span
                      className={
                        `whitespace-nowrap ml-1 font-medium transition-all duration-200
                        ${collapsed ? 'opacity-0 w-0 h-0 overflow-hidden' : 'opacity-100'}
                        ${isActive ? 'text-[#607686] font-semibold' : 'text-[#8BA4B6] group-hover:text-[#21836C]'}
                        text-[15px]`
                      }
                      style={collapsed ? { width: 0, padding: 0, margin: 0 } : { letterSpacing: ".01rem" }}
                    >
                      {label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
        </div>

       
      </div>
    </aside>
  );
}

export default SideBar
